import type { BoardConnection, BoardElement, Point } from '../../api/boards'
import { type Handle, cornersOf, endsOf, handlesOf, pathOf } from './routing'

/**
 * The lines, as one SVG laid over the surface.
 *
 * One `<svg>` for all of them rather than one per line: they are drawn in the same coordinates the
 * elements are placed in, so they sit inside the same transformed layer and cost one element in the
 * document however many lines there are.
 *
 * It is **`pointer-events: none` everywhere except on the lines themselves**, which is what lets a
 * drag on the empty surface pan the board while a line can still be picked up. The visible stroke is
 * thin; a second, transparent stroke runs along it and is what a finger actually hits — a two-pixel
 * target is a target nobody reaches on a touch screen.
 */
export interface ConnectionsProps {
  readonly connections: readonly BoardConnection[]
  readonly elements: ReadonlyMap<string, BoardElement>
  readonly selected: string | null
  /** `false` on a phone: the lines are shown, and nothing about them is dragged. */
  readonly rearrangeable: boolean
  /**
   * How far the board is zoomed.
   *
   * Everything on this layer is drawn in board coordinates, so a two-pixel stroke was half a pixel at
   * a third of the way out — the cards stayed readable and the lines between them all but vanished. A
   * line is a *mark on the drawing* rather than a thing with a size, so its width, the arrow heads
   * that hang off it and the handles are divided by the zoom and stay the same on the screen.
   */
  readonly scale: number
  readonly onSelect: (connection: BoardConnection) => void
  /**
   * A handle has been taken hold of.
   *
   * Which one decides what the drag means: a *corner* is moved, and removed when it is let go of back
   * on the line; the middle of a *leg* puts a new corner in.
   */
  readonly onGrab: (connection: BoardConnection, handle: Handle, event: React.PointerEvent) => void
  /**
   * Takes one corner out.
   *
   * **A double click on the corner**, which is what every drawing tool does and therefore the first
   * thing anybody tries. There was a badge with an × beside the corner here for about an hour; it
   * was one more thing on the surface, it needed a rule of its own for touch, and it answered a
   * question the convention already answers.
   */
  readonly onRemoveCorner: (connection: BoardConnection, index: number) => void
  /** The line being drawn right now, before it has two ends worth keeping. */
  readonly drawing: { readonly from: Point; readonly to: Point } | null
}

export function Connections({
  connections,
  elements,
  selected,
  rearrangeable,
  scale,
  onSelect,
  onGrab,
  onRemoveCorner,
  drawing,
}: ConnectionsProps) {
  /** A width in screen pixels, said in the board coordinates this layer is drawn in. */
  const onScreen = (pixels: number) => pixels / Math.max(scale, 0.01)

  /*
   * The layer is **not** `aria-hidden`, and that took finding: the corner handles are controls —
   * they carry a role, a name and a tab stop — and every one of them was invisible to a keyboard and
   * to a screen reader because the whole layer was hidden. What is genuinely decoration says so one
   * element at a time instead.
   */
  return (
    <svg className="board__lines" overflow="visible">
      <defs>
        {/*
          One marker per colour would be one definition per colour, so the head takes the line's
          own stroke through `context-stroke` where the browser has it and falls back to the ink
          colour where it does not. A head that is the wrong colour is a great deal better than a
          head that is not drawn.
        */}
        <marker
          id="board-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
        </marker>
      </defs>

      {connections.map((line) => {
        const ends = endsOf(line, elements)
        if (ends === null) {
          return null
        }
        const corners = cornersOf(
          ends.from,
          ends.to,
          line.routing,
          line.waypoints,
          ends.fromSide,
          ends.toSide,
        )
        const path = pathOf(corners, line.routing, ends.fromSide, ends.toSide)
        const isSelected = line.id === selected

        return (
          <g key={line.id} className={isSelected ? 'line line--selected' : 'line'}>
            {/* What a finger hits. Invisible, wide, and the only thing on this layer that takes a
                pointer at all. */}
            <path
              className="line__reach"
              aria-hidden="true"
              d={path}
              strokeWidth={onScreen(18)}
              onPointerDown={(event) => {
                event.stopPropagation()
                onSelect(line)
              }}
            />
            <path
              className="line__drawn"
              aria-hidden="true"
              d={path}
              strokeWidth={onScreen(isSelected ? 3 : 2)}
              data-colour={line.colour ?? undefined}
              markerEnd={line.heads === 'none' ? undefined : 'url(#board-arrow)'}
              markerStart={line.heads === 'both' ? 'url(#board-arrow)' : undefined}
            />
            {isSelected &&
              rearrangeable &&
              handlesOf(corners, line.waypoints).map((handle) => {
                const isCorner = handle.kind === 'corner'
                const isEnd = handle.kind === 'end'
                const which =
                  handle.kind === 'corner'
                    ? handle.index
                    : handle.kind === 'leg'
                      ? handle.leg
                      : handle.which
                return (
                  <circle
                    key={`${line.id}-${handle.kind}-${which}`}
                    /*
                      Filled for a corner that is there, hollow for a place one can be put. The two do
                      different things, so they may not look the same — and the corners are drawn
                      last, so where they lie close the one that moves is the one under the pointer.
                    */
                    className={isEnd ? 'line__end' : isCorner ? 'line__corner' : 'line__grab'}
                    aria-hidden={isCorner || isEnd ? undefined : true}
                    cx={handle.at.x}
                    cy={handle.at.y}
                    r={onScreen(isEnd ? 10 : isCorner ? 9 : 6)}
                    strokeWidth={onScreen(2)}
                    /* Reachable without a pointer at all, which is what a keyboard needs: Tab to a
                       corner, Delete takes it out. No badge, no list, no second way of saying it. */
                    tabIndex={isCorner || isEnd ? 0 : undefined}
                    role={isCorner || isEnd ? 'button' : undefined}
                    aria-label={
                      handle.kind === 'corner'
                        ? `Corner ${handle.index + 1} — Delete takes it out`
                        : handle.kind === 'end'
                          ? `${handle.which === 'from' ? 'Start' : 'End'} of this line — drag it somewhere else`
                          : undefined
                    }
                    onPointerDown={(event) => {
                      event.stopPropagation()
                      onGrab(line, handle, event)
                    }}
                    onDoubleClick={(event) => {
                      event.stopPropagation()
                      if (isCorner) {
                        onRemoveCorner(line, handle.index)
                      }
                    }}
                    onKeyDown={(event) => {
                      if (isCorner && (event.key === 'Delete' || event.key === 'Backspace')) {
                        event.preventDefault()
                        // And no further: the board listens for Delete on the window and would take
                        // the whole line off, which is emphatically not what was asked for here.
                        event.stopPropagation()
                        onRemoveCorner(line, handle.index)
                      }
                    }}
                  >
                    <title>
                      {isEnd
                        ? 'Drag this end onto something else, or onto the bare surface'
                        : isCorner
                          ? 'Drag to move this corner. Double-click it to take it out.'
                          : 'Drag to put a corner here'}
                    </title>
                  </circle>
                )
              })}
          </g>
        )
      })}

      {/* The line being drawn, following the pointer. Never a row until it has been let go of. */}
      {drawing !== null && (
        <path
          className="line__drawing"
          aria-hidden="true"
          d={pathOf([drawing.from, drawing.to])}
          strokeWidth={onScreen(2)}
          strokeDasharray={`${onScreen(6)} ${onScreen(4)}`}
          markerEnd="url(#board-arrow)"
        />
      )}
    </svg>
  )
}
